import { ControlOutlined, GlobalOutlined } from '@ant-design/icons'
import FormCard from '@components/FormCard'
import Page from '@components/Page/Page'
import { SettingKey } from '@models/Setting'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { getSettings } from '@store/SettingReducer'
import { getVenues, selectVenues } from '@store/VenueReducer'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { SBRMAction } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import MetadataKey from '@utils/MetadataKey'
import { Permission } from '@utils/permissions'
import { WEB_BOOKING_URL } from '@utils/urls'
import { Button, Card, Flex, Select, Typography } from 'antd'
import { useEffect, useState } from 'react'

const venuesQuery: TableParams = {
  ...initialQuery,
  columnKey: 'name',
  order: 'ascend',
  pagination: { ...initialQuery.pagination, pageSize: 1000, current: 1 },
}

/**
 * Global switch of the public booking website and its embeddable widget
 * (plethore-webapp-frontend). While it is off the site answers 404 and the web
 * API refuses its public endpoints. The links open the live pages, so the
 * team can check them right after switching: the website, a venue's widget
 * page, and the demo restaurant site that opens the widget as a modal.
 */
const IntegrationsWebBookingPage = () => {
  const dispatch = useAppDispatch()
  const venues = useAppSelector(selectVenues())
  const [venueSlug, setVenueSlug] = useState<string>()

  useEffect(() => {
    dispatch(getSettings([SettingKey.WEB_BOOKING_ENABLED]))
    dispatch(getVenues(venuesQuery))
  }, [])

  const open = (path: string) =>
    window.open(`${WEB_BOOKING_URL}${path}`, '_blank', 'noopener,noreferrer')

  return (
    <Page
      permissions={Permission.editSettingsPlatform}
      pageTitle={{ title: 'Web booking', level: 4 }}
    >
      <FormCard
        title="Activate web booking"
        description="Open or close the booking website and its widget. While closed, every page answers 404 and the web API refuses bookings. A change shows on the site within 30 seconds."
        icon={<ControlOutlined />}
        entity={Entity.setting}
        action={SBRMAction.Update}
        metadata={[
          { key: MetadataKey.Setting, value: SettingKey.WEB_BOOKING_ENABLED },
        ]}
      />
      <Card style={{ marginTop: 16 }}>
        <Flex vertical gap={16}>
          <Flex gap={8} align="center">
            <GlobalOutlined />
            <Typography.Text strong>Open the live pages</Typography.Text>
          </Flex>
          <Flex gap={8} wrap>
            <Button
              onClick={() => open('/')}
              disabled={!WEB_BOOKING_URL}
            >
              Website
            </Button>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Venue"
              style={{ minWidth: 240 }}
              value={venueSlug}
              onChange={setVenueSlug}
              options={venues.map((venue) => ({
                value: venue.slug,
                label: venue.name,
              }))}
            />
            <Button
              onClick={() => venueSlug && open(`/embed/${venueSlug}`)}
              disabled={!WEB_BOOKING_URL || !venueSlug}
            >
              Widget (iframe page)
            </Button>
            <Button
              onClick={() =>
                venueSlug &&
                open(`/demo-perruche.html?venue=${encodeURIComponent(venueSlug)}`)
              }
              disabled={!WEB_BOOKING_URL || !venueSlug}
            >
              Demo restaurant site (modal)
            </Button>
          </Flex>
        </Flex>
      </Card>
    </Page>
  )
}

export default IntegrationsWebBookingPage
